import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Switch } from 'pretty-checkbox-react';
/* actions */
import { addPermissionsGroup } from '../../redux/actions/permissionsGroup';
import { getAllCompanies } from '../../redux/actions/companies';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
/* form-validation */
import schema from '../../form-validation/permissionsGroup';
/* utils */
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption,
  handleChangeSelect
} from '../../utils/select';
import sortable from '../../utils/sortable';
import handleChangeDefault from '../../utils/handleChangeDefault';
/* outhers */
import api from '../../api';
import {
  idPermissionsGroup4Case,
  idCompany4Case
} from '../../utils/defaultPermissions';
import hideItems from './hideItems';

function Add (props) {
  const { handleModalClose } = props;
  const INITIAL_VALUES = {
    name: '',
    description: '',
    company: '',
    permissions: []
  };
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, reset, setError, setValue } = useForm(
    {
      validationSchema: schema,
      reValidateMode: 'onSubmit'
    }
  );
  const permissionsGroup = useSelector(state => state.permissionsGroup);
  const [data, setData] = useState(INITIAL_VALUES);
  const companies = useSelector(state => state.companies.data);
  const [companyOptions, setSpecieOptions] = useState([firstOption]);
  const sessionUser = useSelector(state => state.authentications.user);

  useEffect(() => {
    const { permissionsGroup } = sessionUser;
    const query =
      permissionsGroup._id && permissionsGroup._id === idPermissionsGroup4Case
        ? {}
        : { _id: permissionsGroup.company };
    dispatch(getAllCompanies(query));
  }, [dispatch]);

  useEffect(() => {
    if (companies.length) {
      const options = companies
        .map(company => ({
          value: company._id,
          label: `${company.name}`,
          sortValue: `${company.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setSpecieOptions([...options]);
    }
  }, [companies, setSpecieOptions]);

  const handleChangeGuides = evt => {
    const { name, checked } = evt.target;
    const permissions = data.permissions.map(guide => {
      if (guide.id === name) {
        return { ...guide, enabled: checked };
      }
      return { ...guide };
    });
    setData({ ...data, permissions });
  };

  const handleChangeMenu = (evt, guide) => {
    const { name, checked } = evt.target;
    const permissions = data.permissions.map(_guide => {
      if (_guide.id === guide.id && guide.items && guide.enabled) {
        _guide.items = guide.items.map(menu => {
          if (menu.id === name) {
            return { ...menu, enabled: checked };
          }
          return { ...menu };
        });
      }
      return { ..._guide };
    });
    setData({ ...data, permissions });
  };

  const handleChangeSubMenu = (evt, guide, menu) => {
    const { name, checked } = evt.target;
    const permissions = data.permissions.map(_guide => {
      if (_guide.id === guide.id && guide.items && guide.enabled) {
        _guide.items = guide.items.map(_menu => {
          if (_menu.id === menu.id && menu.items && menu.enabled) {
            _menu.items = menu.items.map(submenu => {
              if (submenu.id === name) {
                return { ...submenu, enabled: checked };
              }
              return { ...submenu };
            });
          }
          return { ..._menu };
        });
      }
      return { ..._guide };
    });
    setData({ ...data, permissions });
  };

  const handleChangeSubMenuOptions = (evt, guide, menu, nameSubMenu) => {
    const { name, checked } = evt.target;
    const permissions = data.permissions.map(_guide => {
      if (_guide.id === guide.id && guide.items && guide.enabled) {
        _guide.items = guide.items.map(_menu => {
          if (_menu.id === menu.id && menu.items && menu.enabled) {
            _menu.items = menu.items.map(submenu => {
              if (submenu.id === nameSubMenu) {
                const options = {
                  ...submenu.options,
                  [name]: checked
                };
                return { ...submenu, options };
              }
              return { ...submenu };
            });
          }
          return { ..._menu };
        });
      }
      return { ..._guide };
    });
    setData({ ...data, permissions });
  };

  useEffect(() => {
    if (data.company) {
      (async function () {
        try {
          const response = await api().get('/api/companies', {
            params: { _id: data.company }
          });
          if (response.data && response.data[0]) {
            const permissions = [...response.data[0].permissions];
            const newPermissions = permissions.reduce((guides, guide) => {
              if (guide.enabled) {
                const newGuide = { ...guide };
                if (guide.items) {
                  const items = guide.items.reduce((menus, menu) => {
                    if (menu.enabled) {
                      let items = [];
                      if (menu.items) {
                        items = menu.items.filter(submenu => {
                          return (
                            submenu.enabled &&
                            !(
                              submenu.id === 'companies' &&
                              sessionUser.permissionsGroup._id !==
                                idPermissionsGroup4Case
                            )
                          );
                        });
                      }
                      const submenus = items.map(submenu => {
                        /*
                        c: criar
                        l: listar
                        u: atualizar
                        d: deletar
                        r: restaurar
                        h: ocultar dados sensiveis
                        */
                        const options = submenu.options
                          ? { ...submenu.options }
                          : {
                            c: true,
                            l: true,
                            u: true,
                            d: true,
                            r: true,
                            h: false
                          };
                        return {
                          ...submenu,
                          options
                        };
                      });
                      menus.push({ ...menu, items: submenus });
                    }
                    return menus;
                  }, []);
                  newGuide.items = [...items];
                }
                guides.push({ ...newGuide });
              }
              return guides;
            }, []);
            setData({ ...data, permissions: newPermissions });
          }
        } catch (err) {
          console.error('Error: ', err);
          setData({ ...data, permissions: [] });
        }
      })();
    } else {
      setData({ ...data, permissions: [] });
    }
  }, [data.company]);

  const submit = () => {
    const resetForm = () => {
      reset();
      setData({ ...INITIAL_VALUES });
    };
    dispatch(addPermissionsGroup(data, resetForm, submitButton.current));
  };

  return (
    <div className="container-fluid">
      {permissionsGroup.addSuccess && (
        <AlertMsg
          type="success"
          msg="Grupo de permissões cadastrado com sucesso"
        />
      )}
      {permissionsGroup.errorContent &&
        !permissionsGroup.errorContent.message.includes('400') &&
        permissionsGroup.addError && (
        <AlertMsg
          type="danger"
          msg="Falha durante cadastro do grupo de permissões"
        />
      )}
      {permissionsGroup.errorContent &&
        permissionsGroup.errorContent.message.includes('400') &&
        permissionsGroup.addError && (
        <AlertMsg
          type="danger"
          msg="Um grupo de permissões com o nome informado já está cadastrado"
        />
      )}
      {!permissionsGroup.addSuccess && !permissionsGroup.addError && (
        <AlertMsg type="info" msg="Área cadastro grupo de permissões" />
      )}
      <FormHeader title="Cadastrar Grupo de Permissões" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="name">
                Nome<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                autoComplete="off"
                value={data.name}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.name && errors.name.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="company">
                Empresa<span className="field-required">*</span>
              </label>
              <Select
                className="col p-0"
                name="company"
                ref={register}
                onChange={evt =>
                  handleChangeSelect(
                    { ...evt, name: 'company' },
                    data,
                    setData,
                    setError,
                    setValue
                  )
                }
                value={companyOptions.find(
                  company => company.value === data.company
                )}
                options={companyOptions}
                isSearchable={true}
                theme={customThemes}
                styles={customStyle}
                filterOption={customFilter}
                noOptionsMessage={() => 'Nenhum registro encontrado'}
              />
              {errors.company && errors.company.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">
            Descrição<span className="field-required">*</span>
          </label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            rows="3"
            value={data.description}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          ></textarea>
          {errors.description && errors.description.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="permissions">
            Permissões do Sistema<span className="field-required">*</span>
          </label>
          {data.permissions.length <= 0 && (
            <p>- Nenhuma empresa seleciona até o momento</p>
          )}
          {data.permissions.map((guide, i) => {
            return (
              <div key={i} className="form-group pl-3">
                <div className="checkbox">
                  <Switch
                    type="checkbox"
                    color="success"
                    name={guide.id}
                    onChange={handleChangeGuides}
                    checked={guide.enabled}
                  ></Switch>
                  <span className="ml-2">
                    <i className={guide.icon} /> GUIA {guide.tab}
                  </span>
                </div>
                {guide.items &&
                  guide.items.map((menu, i) => {
                    return (
                      <div key={i} className="mb-1 pl-5">
                        <div className="checkbox">
                          <Switch
                            type="checkbox"
                            color="success"
                            name={menu.id}
                            onChange={evt => handleChangeMenu(evt, guide)}
                            checked={guide.enabled ? menu.enabled : false}
                          ></Switch>
                          <span className="ml-2">
                            <i className={menu.icon} /> {menu.title}
                          </span>
                        </div>
                        {menu.items &&
                          menu.items.map((submenu, i) => {
                            if (
                              submenu.id === 'companies' &&
                              data.company !== idCompany4Case
                            ) {
                              return false;
                            }
                            return (
                              <div key={i} className="mb-2 pl-4">
                                <div className="checkbox">
                                  <Switch
                                    type="checkbox"
                                    color="primary"
                                    name={submenu.id}
                                    onChange={evt =>
                                      handleChangeSubMenu(evt, guide, menu)
                                    }
                                    checked={
                                      guide.enabled && menu.enabled
                                        ? submenu.enabled
                                        : false
                                    }
                                  ></Switch>
                                  <span className="ml-2">
                                    <i className={submenu.icon} />{' '}
                                    {submenu.title}
                                  </span>
                                </div>
                                {/* start options */}
                                {!hideItems.includes(submenu.id) && (
                                  <div className="ml-3">
                                    <Switch
                                      type="checkbox"
                                      color="info"
                                      name="c"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.c
                                      }
                                    >
                                      Criar
                                    </Switch>
                                    <Switch
                                      type="checkbox"
                                      color="info"
                                      name="l"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.l
                                      }
                                    >
                                      Listar
                                    </Switch>
                                    <Switch
                                      type="checkbox"
                                      color="info"
                                      name="u"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.l &&
                                        submenu.options.u
                                      }
                                    >
                                      Atualizar
                                    </Switch>
                                    <Switch
                                      type="checkbox"
                                      color="info"
                                      name="d"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.l &&
                                        submenu.options.d
                                      }
                                    >
                                      Deletar
                                    </Switch>
                                    <Switch
                                      type="checkbox"
                                      color="info"
                                      name="r"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.l &&
                                        submenu.options.r
                                      }
                                    >
                                      Restaurar
                                    </Switch>
                                    <Switch
                                      type="checkbox"
                                      color="info"
                                      name="h"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.h
                                      }
                                    >
                                      Ocultar Dados Sensíveis
                                    </Switch>
                                  </div>
                                )}
                                {/* end options */}
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>
        {handleModalClose ? (
          <div className="container-fluid p-0">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleModalClose}
            >
              <i className="fa fa-times"></i>
              &nbsp;Fechar
            </button>
            <button
              type="submit"
              ref={submitButton}
              className="btn btn-primary float-right"
            >
              <i className="fa fa-save"></i>
              &nbsp;Salvar
            </button>
          </div>
        ) : (
          <FormFooter url="/permissions_group" reference={submitButton} />
        )}
      </form>
    </div>
  );
}

Add.propTypes = {
  handleModalClose: PropTypes.func
};

export default Add;
